import type { ExportListParams, ExportStatus } from '@/lib/api/reports-client.js';
import { cn } from '@/lib/utils.js';

interface ExportsFiltersProps {
  params: ExportListParams;
  onParamsChange: (next: ExportListParams) => void;
}

const STATUS_OPTIONS: Array<{ value: ExportStatus | ''; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'queued', label: 'En cola' },
  { value: 'running', label: 'En curso' },
  { value: 'completed', label: 'Completado' },
  { value: 'failed', label: 'Fallido' },
  { value: 'cancelled', label: 'Cancelado' },
];

const FORMAT_OPTIONS: Array<{ value: 'csv' | 'xlsx' | 'pdf' | ''; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'csv', label: 'CSV' },
  { value: 'xlsx', label: 'Excel' },
  { value: 'pdf', label: 'PDF' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

const selectCls = cn(
  'rounded border border-[var(--color-qup-border-dark)] bg-[var(--color-qup-bg-card-dark)]',
  'px-2 py-1.5 text-xs text-[var(--color-qup-text-dark)]',
  'focus:outline-none focus:ring-2 focus:ring-[var(--color-qup-primary)]',
);

export function ExportsFilters({ params, onParamsChange }: ExportsFiltersProps) {
  function handleStatus(value: string) {
    const next: ExportListParams = { ...params, page_number: 1 };
    if (value === '') {
      const { status: _omit, ...rest } = next;
      onParamsChange(rest);
      return;
    }
    next.status = value as ExportStatus;
    onParamsChange(next);
  }

  function handleFormat(value: string) {
    const next: ExportListParams = { ...params, page_number: 1 };
    if (value === '') {
      const { format: _omit, ...rest } = next;
      onParamsChange(rest);
      return;
    }
    next.format = value as 'csv' | 'xlsx' | 'pdf';
    onParamsChange(next);
  }

  function handlePageSize(value: string) {
    onParamsChange({ ...params, page_size: Number(value), page_number: 1 });
  }

  return (
    <div data-testid="exports-filters" className="flex flex-wrap items-center gap-3">
      {/* Status filter */}
      <div className="flex items-center gap-1.5">
        <label htmlFor="filter-status" className="text-xs text-[var(--color-qup-text-muted-dark)]">
          Estado
        </label>
        <select
          id="filter-status"
          value={params.status ?? ''}
          onChange={(e) => handleStatus(e.target.value)}
          className={selectCls}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Format filter */}
      <div className="flex items-center gap-1.5">
        <label htmlFor="filter-format" className="text-xs text-[var(--color-qup-text-muted-dark)]">
          Formato
        </label>
        <select
          id="filter-format"
          value={params.format ?? ''}
          onChange={(e) => handleFormat(e.target.value)}
          className={selectCls}
        >
          {FORMAT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Page size */}
      <div className="flex items-center gap-1.5">
        <label
          htmlFor="filter-page-size"
          className="text-xs text-[var(--color-qup-text-muted-dark)]"
        >
          Por página
        </label>
        <select
          id="filter-page-size"
          value={params.page_size ?? 25}
          onChange={(e) => handlePageSize(e.target.value)}
          className={selectCls}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
